import type { CoachingNote } from "@/lib/types";
import { TacticalBoardView } from "@/features/canvas/TacticalBoardView";
import {
  sceneFromOnDisk,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import { shortDateLabel } from "@/features/feedback/feedbackHelpers";
import { NOTE_CATEGORIES, NOTE_CATEGORY_LABELS } from "./Composer";

export type RecentObservationsRailProps = {
  observations: CoachingNote[];
  loading?: boolean;
  activeObservationId?: number | null;
  onOpenObservation: (note: CoachingNote) => void;
};

const EMPTY_CARD =
  "flex h-[4.5rem] w-full items-center rounded border border-dashed " +
  "border-rule px-3 font-mono text-xs text-ink-muted";

function observationTitle(note: CoachingNote): string {
  return (
    note.title?.trim() ||
    note.event_title?.trim() ||
    note.player_summary?.trim() ||
    "Observation"
  );
}

function observationMeta(note: CoachingNote): string {
  const parts: string[] = [];
  if (note.event_type) {
    parts.push(`${note.event_type[0].toUpperCase()}${note.event_type.slice(1)}`);
  }
  if (note.category) {
    const category = NOTE_CATEGORIES.includes(
      note.category as (typeof NOTE_CATEGORIES)[number],
    )
      ? NOTE_CATEGORY_LABELS[note.category as (typeof NOTE_CATEGORIES)[number]]
      : note.category;
    parts.push(category);
  }
  if (note.player_ids.length > 0) {
    parts.push(
      `${note.player_ids.length} player${note.player_ids.length === 1 ? "" : "s"}`,
    );
  }
  const updated = shortDateLabel(note.updated_at);
  if (updated) parts.push(`Updated ${updated}`);
  return parts.join(" · ");
}

function boardScene(note: CoachingNote): BoardScene | null {
  if (!note.tactical_board_json) return null;
  const parsed = sceneFromOnDisk(note.tactical_board_json);
  return parsed.ok ? parsed.value : null;
}

export function RecentObservationsRail({
  observations,
  loading,
  activeObservationId,
  onOpenObservation,
}: RecentObservationsRailProps) {
  return (
    <section
      className="rounded border border-rule bg-surface p-4"
      data-testid="coach-review-observations-rail"
    >
      <div className="mb-2 flex items-center gap-2">
        <h4 className="font-mono text-[10px] uppercase tracking-mono text-ink-muted">
          Recent observations
        </h4>
        <span className="rounded-full bg-field px-1.5 py-px font-mono text-[10px] text-ink-secondary">
          {observations.length}
        </span>
      </div>

      {loading ? (
        <p className={EMPTY_CARD}>Loading observations…</p>
      ) : observations.length === 0 ? (
        <p
          className={EMPTY_CARD}
          data-testid="coach-review-observations-empty"
        >
          No tactical observations yet. Use Mark observation to save this board.
        </p>
      ) : (
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          data-testid="coach-review-observations-strip"
        >
          {observations.map((note) => {
            const active = activeObservationId === note.id;
            const scene = boardScene(note);
            return (
              <button
                key={note.id}
                type="button"
                onClick={() => onOpenObservation(note)}
                data-testid="coach-review-observation-card"
                data-coach-note-id={note.id}
                aria-pressed={active}
                className={[
                  "flex w-64 flex-shrink-0 gap-2 rounded border-l-[3px] border-l-good px-2.5 py-2 text-left transition-colors",
                  active ? "bg-field" : "bg-elevated hover:bg-field",
                ].join(" ")}
              >
                <div className="flex h-14 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-canvas">
                  {scene ? (
                    <TacticalBoardView
                      scene={scene}
                      width={96}
                      passThrough
                      hideLabels
                    />
                  ) : (
                    <span className="px-2 text-center font-mono text-[10px] text-ink-muted">
                      No board
                    </span>
                  )}
                </div>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 block font-display text-sm font-semibold text-ink">
                    {observationTitle(note)}
                  </span>
                  <span className="mt-1 line-clamp-2 block font-mono text-[10px] uppercase tracking-mono text-ink-muted">
                    {observationMeta(note)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
